import { Fragment } from 'react';
import { useParams } from 'react-router';
import { Alert, Group, Stack, Modal, Button, Loader, Center, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ViewLayout, ViewPanel } from '@ayocore/platform/client/components/EntityViewLayout';
import { CleanTabs } from '@ayocore/platform/client/components/CleanTabs';
import { useConfirm } from '@ayocore/platform/client/hooks/useConfirm';
import { useUrlTabState } from '@ayocore/platform/client/hooks/useUrlTabState';
import { useDealViewController } from './useDealViewController';
import { useDuplicateDeal } from './useDuplicateDeal';
import {
  DealHeader,
  DealInfo,
  DealDates,
  DealStatusHistory,
  DealFinancialBlock,
  DealStatusChangeModal,
} from './components';
import { DealRowsPanel, DealExpenses } from '../components';
import { EntityFiles } from '@ayocore/platform/filemanager/client/components';
import { REPOSITORIES } from '@ayocore/platform/common/types/repositories.generated';
import { DealEvents } from '../events/DealEvents';
import { EditDealForm } from '../forms/EditDealForm';
import { DealViewExtensions } from './DealViewExtensions';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';

/** Дата + время в формате ДД.ММ.ГГГГ, ЧЧ:ММ (как в карточке лида/партнёра). */
function formatDealDateTime(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface DealViewProps {
  dealId?: number;
  isModal?: boolean;
  showEventList?: boolean;
  /** Вкладка, которая будет активна при открытии (по умолчанию 'main') */
  initialTab?: string;
}

export function DealView({
  dealId: propDealId,
  isModal = false,
  showEventList = true,
  initialTab = 'main',
}: DealViewProps) {
  const { id } = useParams<{ id: string }>();
  const dealId = propDealId || (id ? parseInt(id, 10) : null);

  const [activeTab, setActiveTab] = useUrlTabState('deal_tab', initialTab, { enabled: !isModal });
  const confirm = useConfirm();
  // Хук вызываем до любых early-return'ов, чтобы количество вызовов хуков было
  // одинаковым между рендерами (правило хуков React). Принимает number | null.
  const handleDuplicate = useDuplicateDeal(dealId);

  if (!dealId || Number.isNaN(dealId)) {
    return (
      <Alert color='red' title='Ошибка'>
        Неверный ID сделки
      </Alert>
    );
  }

  const editDealModal = useModal(EditDealForm);

  const {
    deal,
    statusHistory,
    financialSummary,
    loading,
    error,
    permissions,
    modals,
    statusChangeTarget,
    statusChangeError,
    statusChanging,
    openDelete,
    closeDelete,
    deleteDeal,
    reload,
    updateDealField,
    openStatusChange,
    closeStatusChange,
    changeStatus,
    checkCanArchive,
    archiveDeal,
    unarchiveDeal,
    cancelDeal,
  } = useDealViewController(dealId, isModal);

  // Инлайн-модалка удаления (не через useModal): z-index из общего аллокатора,
  // иначе при открытии DealView в дравере модалка окажется под ним.
  const deleteModalZIndex = useSurfaceZIndex(modals.delete);

  if (loading) {
    return (
      <Center style={{ height: '100%', minHeight: 400 }}>
        <Loader size='lg' />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color='red' title='Ошибка'>
        {error}
      </Alert>
    );
  }

  if (!deal) {
    return (
      <Alert color='yellow' title='Не найдено'>
        Сделка не найдена
      </Alert>
    );
  }

  const handleEditSuccess = () => {
    reload();
  };

  const handleArchive = async () => {
    const check = await checkCanArchive();
    if (!check.allowed) {
      notifications.show({
        color: 'red',
        message: check.message || 'Недостаточно прав для архивирования',
      });
      return;
    }
    if (check.message) {
      const ok = await confirm(check.message, {
        title: 'Архивирование сделки',
        confirmLabel: 'Архивировать',
      });
      if (!ok) return;
    }
    const err = await archiveDeal();
    if (err) notifications.show({ color: 'red', message: err });
  };

  const handleUnarchive = async () => {
    const err = await unarchiveDeal();
    if (err) notifications.show({ color: 'red', message: err });
  };

  const handleCancel = async () => {
    const err = await cancelDeal();
    if (err) notifications.show({ color: 'red', message: err });
  };

  // Расширения от клиентских модулей
  const extensionPayload = { deal, reload };
  const allExtensionTabs = DealViewExtensions.tabs
    .getAll()
    .map(fn => fn(extensionPayload))
    .filter((tab): tab is NonNullable<typeof tab> => tab !== null);
  // Вкладки-замены: ключ встроенной вкладки → расширение
  const tabOverrides = Object.fromEntries(
    allExtensionTabs.filter(tab => tab.overrides).map(tab => [tab.overrides!, tab])
  );
  // Вкладки-добавления: без overrides, рендерятся после встроенных
  const extraTabs = allExtensionTabs.filter(tab => !tab.overrides);
  const extraHeaderActions = DealViewExtensions.headerActions.getAll();
  const extraMainBlocks = DealViewExtensions.mainPanelBlocks.getAll();
  const shouldHideEventList = DealViewExtensions.hideEventList
    .getAll()
    .some(fn => fn(extensionPayload));
  const effectiveShowEventList = showEventList && !shouldHideEventList;

  const mainContent = (
    <>
      <DealHeader
        deal={deal}
        canEdit={permissions.canEdit}
        canDelete={permissions.canDelete}
        permissionsLoading={permissions.loading}
        onArchive={!deal.archived ? handleArchive : undefined}
        onUnarchive={deal.archived ? handleUnarchive : undefined}
        onEdit={() =>
          editDealModal.open(
            { dealId, onDealUpdated: handleEditSuccess, title: 'Редактировать сделку' },
            { inputSize: 'xs' }
          )
        }
        onCancel={handleCancel}
        onDelete={openDelete}
        onDuplicate={permissions.canEdit ? handleDuplicate : undefined}
        extraActions={extraHeaderActions.map((fn, i) => (
          <Fragment key={i}>{fn(extensionPayload)}</Fragment>
        ))}
      />

      <CleanTabs value={activeTab} onChange={v => setActiveTab(v ?? initialTab)}>
        <CleanTabs.List>
          <CleanTabs.Tab value='main'>Основное</CleanTabs.Tab>
          <CleanTabs.Tab value='rows'>{tabOverrides['rows']?.label ?? 'Позиции'}</CleanTabs.Tab>
          {tabOverrides['documents'] && (
            <CleanTabs.Tab value='documents'>{tabOverrides['documents'].label}</CleanTabs.Tab>
          )}
          {!tabOverrides['expenses']?.hidden && (
            <CleanTabs.Tab value='expenses'>
              {tabOverrides['expenses']?.label ?? 'Расходы'}
            </CleanTabs.Tab>
          )}
          <CleanTabs.Tab value='files'>Файлы</CleanTabs.Tab>
          {extraTabs.map(tab => (
            <CleanTabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </CleanTabs.Tab>
          ))}
        </CleanTabs.List>

        <CleanTabs.Panel value='main'>
          <Stack gap='xs'>
            <DealFinancialBlock deal={deal} financialSummary={financialSummary} />

            <DealStatusHistory
              statusHistory={statusHistory}
              currentStatusId={deal.deal_status_id}
              dealTypeId={deal.deal_type_id}
              onStatusClick={permissions.canEdit ? openStatusChange : undefined}
            />

            <DealInfo
              deal={deal}
              canEdit={permissions.canEdit && !permissions.loading}
              onPartnerSave={id => void updateDealField('partner_id', id)}
              onOwnerSave={id => void updateDealField('deal_owner_id', id)}
              onDescriptionSave={v => void updateDealField('description', v)}
              onNotesSave={v => void updateDealField('notes', v)}
            />

            <DealDates
              deal={deal}
              canEdit={permissions.canEdit && !permissions.loading}
              canEditCloseDate={permissions.canEditCloseDate && !permissions.loading}
              onDeliveryDatePlanSave={date =>
                void updateDealField('delivery_date_plan', date ? date.toISOString() : null)
              }
              onCloseDateFactSave={date =>
                void updateDealField('close_date_fact', date ? date.toISOString() : null)
              }
            />

            {extraMainBlocks.map((fn, i) => (
              <Fragment key={i}>{fn(extensionPayload)}</Fragment>
            ))}

            <ViewPanel>
              <Group gap='xs'>
                <Text size='xs' c='dimmed'>
                  Создана: {formatDealDateTime(deal.created_at)}
                  {deal.created_by?.name ? ` · ${deal.created_by.name}` : ''}
                </Text>
                <Text size='xs' c='dimmed'>
                  Обновлена: {formatDealDateTime(deal.updated_at)}
                  {deal.updated_by?.name ? ` · ${deal.updated_by.name}` : ''}
                </Text>
              </Group>
            </ViewPanel>
          </Stack>
        </CleanTabs.Panel>

        <CleanTabs.Panel value='rows'>
          {tabOverrides['rows']?.content ?? <DealRowsPanel dealId={dealId} dealUuid={deal.uuid} />}
        </CleanTabs.Panel>

        {tabOverrides['documents'] && (
          <CleanTabs.Panel value='documents'>{tabOverrides['documents'].content}</CleanTabs.Panel>
        )}

        {!tabOverrides['expenses']?.hidden && (
          <CleanTabs.Panel value='expenses'>
            {tabOverrides['expenses']?.content ?? (
              <DealExpenses deal={deal} canEdit={permissions.canEdit} onReload={reload} />
            )}
          </CleanTabs.Panel>
        )}

        <CleanTabs.Panel value='files'>
          <EntityFiles
            entityUuid={deal.uuid}
            entityType={REPOSITORIES.CRM_DEAL}
            directory='deals'
          />
        </CleanTabs.Panel>

        {extraTabs.map(tab => (
          <CleanTabs.Panel key={tab.value} value={tab.value}>
            {tab.content}
          </CleanTabs.Panel>
        ))}
      </CleanTabs>
    </>
  );

  return (
    <>
      <ViewLayout storageKey={effectiveShowEventList ? 'crm.dealView.leftPanelPercent' : undefined}>
        <ViewLayout.Stack>{mainContent}</ViewLayout.Stack>
        {effectiveShowEventList && (
          <ViewLayout.Stack>
            <DealEvents deal={{ id: dealId, uuid: deal.uuid }} />
          </ViewLayout.Stack>
        )}
      </ViewLayout>

      <DealStatusChangeModal
        opened={modals.statusChange}
        currentStatus={deal.deal_status ?? null}
        targetStatus={statusChangeTarget}
        error={statusChangeError}
        changing={statusChanging}
        onConfirm={() => void changeStatus()}
        onClose={closeStatusChange}
      />

      <Modal
        opened={modals.delete}
        onClose={closeDelete}
        title='Удалить сделку'
        size='sm'
        zIndex={deleteModalZIndex}
      >
        <Stack gap='md'>
          <Text>
            Вы уверены, что хотите удалить сделку{' '}
            <strong>
              #{deal.id} {deal.partner?.name ?? deal.partner?.full_name ?? ''}
            </strong>
            ?
          </Text>
          <Text size='sm' c='dimmed'>
            Это действие нельзя отменить.
          </Text>
          <Group justify='flex-end' gap='sm'>
            <Button variant='light' onClick={closeDelete} color='gray' size='xs'>
              Отмена
            </Button>
            <Button variant='filled' color='red' size='xs' onClick={() => void deleteDeal()}>
              Удалить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
